import React, { useState } from 'react';
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, TextField, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { copyBackupCodes, downloadBackupCodes, printBackupCodes } from '../../utils/backupCodes';

interface TwoFactorEnrollProps {
    challengeId: string;
    qrDataUri: string;
    secret: string;
    onSuccess: (backupCodes: string[]) => void;
    onCancel: () => void;
}

const TwoFactorEnroll: React.FC<TwoFactorEnrollProps> = ({ challengeId, qrDataUri, secret, onSuccess, onCancel }) => {
    const { completeTwoFactorLogin } = useAuth();
    const { t } = useTranslation(['auth', 'common']);
    const [code, setCode] = useState('');
    const [rememberDevice, setRememberDevice] = useState(true);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
    const [savedConfirmed, setSavedConfirmed] = useState(false);
    const [copied, setCopied] = useState(false);

    const handleCopy = async (codes: string[]) => {
        await copyBackupCodes(codes);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleConfirm = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            const result = await completeTwoFactorLogin(challengeId, code, rememberDevice);
            if (result.success && result.backupCodes) {
                setBackupCodes(result.backupCodes);
            } else if (result.success) {
                // Success but no backup codes returned — malformed response
                setError(t('twoFactorEnroll.noBackupCodes'));
            } else if (!result.success) {
                setError(result.error || t('twoFactor.invalidCode'));
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (backupCodes) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="h5" fontWeight={600} mb={2}>
                    {t('backupCodes.saveTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                    {t('backupCodes.saveHint')}
                </Typography>
                <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2, fontFamily: 'monospace' }}>
                    {backupCodes.map((c) => <div key={c}>{c}</div>)}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Button size="small" onClick={() => handleCopy(backupCodes)}>{copied ? t('backupCodes.copied') : t('backupCodes.copy')}</Button>
                    <Button size="small" onClick={() => downloadBackupCodes(backupCodes)}>{t('common:actions.download')}</Button>
                    <Button size="small" onClick={() => printBackupCodes(backupCodes, { title: t('backupCodes.printTitle'), note: t('backupCodes.printNote') })}>{t('common:actions.print')}</Button>
                </Box>
                <FormControlLabel
                    control={<Checkbox checked={savedConfirmed} onChange={(e) => setSavedConfirmed(e.target.checked)} />}
                    label={t('backupCodes.savedConfirm')}
                    sx={{ mb: 2 }}
                />
                <Button
                    variant="contained"
                    fullWidth
                    disabled={!savedConfirmed}
                    onClick={() => onSuccess(backupCodes)}
                >
                    {t('twoFactorEnroll.continue')}
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" fontWeight={600} mb={2}>
                {t('twoFactorEnroll.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
                {t('twoFactorEnroll.intro')}
            </Typography>

            <Box textAlign="center" sx={{ mb: 2 }}>
                <img src={qrDataUri} alt={t('twoFactorEnroll.qrAlt')} style={{ width: 200, height: 200 }} />
            </Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
                <Trans t={t} i18nKey="twoFactorEnroll.manualEntry" values={{ secret }} components={{ strong: <strong /> }} />
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <form onSubmit={handleConfirm}>
                <TextField
                    label={t('twoFactor.authenticationCode')}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    fullWidth
                    required
                    size="small"
                    sx={{ mb: 2 }}
                    autoFocus
                />
                <FormControlLabel
                    control={<Checkbox checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)} />}
                    label={t('twoFactor.rememberDevice')}
                    sx={{ mb: 2 }}
                />
                <Button type="submit" variant="contained" fullWidth disabled={isLoading} sx={{ mb: 2 }}>
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : t('common:actions.confirm')}
                </Button>
            </form>

            <Box textAlign="center">
                <Button variant="text" onClick={onCancel}>{t('common:actions.cancel')}</Button>
            </Box>
        </Box>
    );
};

export default TwoFactorEnroll;
